/**
 * schema.org JSON-LD builders.
 *
 * Centralized so every tool emits the same shape and we can update fields
 * (e.g. add aggregateRating later) in one place.
 */

import { SITE } from './toolRegistry';
import type { ToolDefinition } from './toolRegistry';

export interface WebAppLdOptions {
  tool: ToolDefinition;
  categoryName: string;
  url: string;
}

/**
 * WebApplication schema for a tool page.
 *
 * Refs: https://schema.org/WebApplication
 *
 * NOTE: aggregateRating is intentionally omitted. Per Google's structured
 * data guidelines, ratings should only be emitted when there is a real,
 * verified rating — fabricated ratings can be considered spammy structured
 * data. Re-evaluate once we have a real rating source (PostHog survey, G2,
 * etc.).
 */
export function buildWebApplicationLd({ tool, categoryName, url }: WebAppLdOptions) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: tool.name,
    description: tool.seo.description,
    url,
    applicationCategory: 'DeveloperApplication',
    applicationSubCategory: categoryName,
    operatingSystem: 'Any',
    permissions: 'browser',
    browserRequirements: 'Requires JavaScript',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    isAccessibleForFree: true,
    inLanguage: SITE.locale,
    keywords: tool.seo.keywords.join(', '),
    publisher: {
      '@type': 'Organization',
      name: SITE.name,
      url: SITE.url,
    },
  };
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function buildBreadcrumbLd(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildFAQPageLd(faqs: ToolDefinition['faqs']) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };
}

export function buildHowToLd(steps: ToolDefinition['howToSteps'], name: string, description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export function buildOrganizationLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE.url}/icon.svg`,
    },
    sameAs: [
      // Add real social URLs once accounts exist.
    ],
  };
}

export function buildWebSiteLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    inLanguage: SITE.locale,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE.url}/tools?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildItemListLd(name: string, items: Array<{ name: string; url: string; description?: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      description: item.description,
      url: item.url,
    })),
  };
}